// ════════════════════════════════════════════════════════════════════
// Contexte de l'utilisateur transmis par la plateforme (SEC-5)
// La plateforme ouvre l'assistant dans une iframe dont l'URL porte entreprise_id,
// user_id, email, user_api_hash (identifiant d'API de l'utilisateur) et, sur une de
// ses pages, password. Laissés dans l'URL, ils finissent dans les journaux nginx
// (ligne de requête, Referer des requêtes suivantes), l'historique du navigateur et
// les liens copiés. Au démarrage : contexte lu une fois, paramètres retirés de l'URL
// visible. Le contexte est attaché à l'entrée d'historique (history.state, relu au
// rechargement et au retour arrière) et noté comme dernier contexte remis dans l'onglet
// (sessionStorage) : une entrée n'est reprise que si elle porte ce dernier contexte,
// pour que l'utilisateur suivant du même onglet ne reprenne jamais celui du précédent.
// Aucun accès direct à window : location, history et storage sont injectés (tests).
// ════════════════════════════════════════════════════════════════════

/** Clé du contexte dans history.state et dans sessionStorage (propre à l'onglet, effacé à sa fermeture). */
export const USER_CONTEXT_KEY = 'digitrans_user_context';

// Paramètres d'identité : un seul suffit pour que l'URL fasse foi
const IDENTITY_PARAMS = ['entreprise_id', 'user_id', 'email', 'user_api_hash'];
// Profil de l'utilisateur (décision du 3 novembre 2026) : code de la liste du chef de projet et
// intitulé du poste ; ils règlent la forme des réponses et des alertes, jamais l'accès aux données
const PROFILE_PARAMS = ['profil', 'poste'];
const PROFIL_RE = /^[a-z][a-z_]{1,39}$/;
const POSTE_MAX = 120;
// Paramètres retirés de l'URL visible. password n'est jamais lu ni conservé.
const SENSITIVE_PARAMS = new Set([...IDENTITY_PARAMS, ...PROFILE_PARAMS, 'password']);

// Profil lu dans l'URL ou l'état : code valide, poste en texte simple ; sinon null
const profilOf = (value) => {
  const code = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return PROFIL_RE.test(code) ? code : null;
};
const posteOf = (value) => {
  const poste = typeof value === 'string' ? value.replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, POSTE_MAX).trim() : '';
  return poste || null;
};
// Champs du profil, présents seulement quand ils sont lisibles (contexte sans profil inchangé)
const profileFields = (profil, poste) => {
  const fields = {};
  if (profilOf(profil)) fields.profil = profilOf(profil);
  if (posteOf(poste)) fields.poste = posteOf(poste);
  return fields;
};

const toInt = (value) => {
  const n = parseInt(value, 10);
  return Number.isSafeInteger(n) ? n : null;
};

// Même forme qu'avant SEC-5 : entreprise_id entier, les autres champs ou null.
// entreprise_id absent, nul ou négatif : aucun contexte (le serveur le refuserait).
function contextFromParams(params) {
  const entreprise_id = toInt(params.get('entreprise_id'));
  if (!(entreprise_id > 0)) return null;
  return {
    entreprise_id,
    user_id: toInt(params.get('user_id')),
    email: params.get('email') || null,
    user_api_hash: params.get('user_api_hash') || null,
    ...profileFields(params.get('profil'), params.get('poste')),
  };
}

// Contexte relu (état d'historique, stockage) : une valeur de forme inattendue ne vaut rien
function validContext(value) {
  if (!value || typeof value !== 'object') return null;
  const { entreprise_id, user_id = null, email = null, user_api_hash = null, profil = null, poste = null } = value;
  const valid = Number.isSafeInteger(entreprise_id) && entreprise_id > 0
    && (user_id === null || Number.isSafeInteger(user_id))
    && (email === null || typeof email === 'string')
    && (user_api_hash === null || typeof user_api_hash === 'string');
  return valid ? { entreprise_id, user_id, email: email || null, user_api_hash: user_api_hash || null, ...profileFields(profil, poste) } : null;
}

const sameContext = (a, b) => IDENTITY_PARAMS.every((field) => a[field] === b[field]);

// Dernier contexte remis par la plateforme dans l'onglet ; null s'il est absent,
// illisible, ou si le stockage est bloqué
function readLastContext(storage) {
  try {
    const raw = storage.getItem(USER_CONTEXT_KEY);
    return raw == null ? null : validContext(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeLastContext(storage, context) {
  try {
    if (context) storage.setItem(USER_CONTEXT_KEY, JSON.stringify(context));
    else storage.removeItem(USER_CONTEXT_KEY);
  } catch {
    /* stockage bloqué (iframe tierce) : contexte valable jusqu'au prochain rechargement */
  }
}

// État de l'entrée d'historique ; une valeur qui n'est pas un objet ne porte aucun contexte
function entryState(history) {
  const { state } = history;
  return state && typeof state === 'object' && !Array.isArray(state) ? state : {};
}

/**
 * Chaîne de requête sans les paramètres sensibles, casse ignorée (Password, EMAIL…).
 * Les autres paramètres restent tels quels, encodage d'origine compris.
 * @param {string} search  `location.search` (avec ou sans « ? »)
 * @returns {string|null}  nouvelle chaîne (« » ou « ?… »), ou null s'il n'y a rien à retirer
 */
export function stripSensitiveParams(search) {
  const parts = String(search ?? '').replace(/^\?/, '').split('&').filter(Boolean);
  const kept = parts.filter((part) => {
    // Nom décodé comme le lit URLSearchParams (pass%77ord, user%5Fapi%5Fhash…)
    const [name = ''] = new URLSearchParams(part).keys();
    return !SENSITIVE_PARAMS.has(name.toLowerCase());
  });
  if (kept.length === parts.length) return null;
  return kept.length ? `?${kept.join('&')}` : '';
}

/**
 * Contexte de l'utilisateur, lu une seule fois au démarrage.
 * - URL avec un paramètre d'identité (entreprise_id, user_id, email ou user_api_hash,
 *   même vide ou invalide) : l'URL fait foi ; son contexte, ou null, devient le dernier
 *   remis dans l'onglet (changement de compte depuis la plateforme) ;
 * - sinon (rechargement, retour arrière) : contexte de l'entrée d'historique, seulement
 *   s'il est identique au dernier remis ; une entrée sans contexte (assistant rouvert sans
 *   paramètres) efface ce dernier ; stockage bloqué : null ;
 * - dans tous les cas, les paramètres sensibles quittent l'URL visible (replaceState :
 *   chemin, autres paramètres, fragment et état conservés, contexte ajouté à l'état,
 *   aucune entrée d'historique).
 * @param {{location: {href: string}, history: History, storage: Storage|null}} env
 * @returns {{entreprise_id: number, user_id: number|null, email: string|null, user_api_hash: string|null, profil?: string, poste?: string}|null}
 */
export function loadUserContext({ location, history, storage }) {
  const url = new URL(location.href);
  const params = new URLSearchParams(url.search);
  const names = new Set([...params.keys()].map((name) => name.toLowerCase()));
  const state = entryState(history);

  let context;
  if (IDENTITY_PARAMS.some((name) => names.has(name))) {
    context = contextFromParams(params);
    writeLastContext(storage, context);
  } else {
    const entry = validContext(state[USER_CONTEXT_KEY]);
    if (!entry) writeLastContext(storage, null);
    const last = entry && readLastContext(storage);
    context = last && sameContext(entry, last) ? entry : null;
  }

  const search = stripSensitiveParams(url.search);
  if (search !== null) {
    url.search = search;
    try {
      history.replaceState({ ...state, [USER_CONTEXT_KEY]: context }, '', url.href);
    } catch {
      /* URL non réécrite (navigateur qui refuse) : le contexte reste utilisable */
    }
  }
  return context;
}

/**
 * `window.sessionStorage`, ou null : la simple lecture de la propriété lève quand le
 * navigateur bloque le stockage (iframe tierce, cookies tiers refusés).
 */
export function sessionStorageOf(win) {
  try {
    return win.sessionStorage ?? null;
  } catch {
    return null;
  }
}
